import React, { useState } from 'react';
import { Button, Badge, Card, Row, Col, Form, Modal } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Project, Goal, Task, TimeEntry } from '../types/models';
import { TimeSheet } from './TimeSheet';
import './GoalDetail.css';

const STATUSES: Task['status'][] = ['pending', 'in-progress', 'completed'];

const STATUS_LABELS: Record<Task['status'], string> = {
  pending: 'Pending',
  'in-progress': 'In Progress',
  completed: 'Completed',
};

const STATUS_COLORS: Record<string, string> = {
  pending: '#6c757d',
  'in-progress': '#0d6efd',
  completed: '#198754',
};

function totalTime(entries: TimeEntry[]): number {
  return (entries ?? [])
    .filter(e => e.endTime !== null)
    .reduce((sum, e) => sum + (new Date(e.endTime!).getTime() - new Date(e.startTime).getTime()), 0);
}

function formatDuration(ms: number): string {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m.toString().padStart(2, '0')}m`;
}

function formatDate(d: Date): string {
  return new Date(String(d).slice(0, 10) + 'T00:00:00').toLocaleDateString();
}

interface GoalDetailProps {
  project: Project;
  goal: Goal;
  onBack: () => void;
  onTaskMove: (taskId: string, newGoalId: string, newStatus: Task['status'], newOrder: number) => void;
  onTaskCreate: (goalId: string, task: Omit<Task, 'id'>, projectId: string) => void;
  onAddTimeEntry: (taskId: string, entry: Omit<TimeEntry, 'id'>) => void;
  onDeleteTimeEntry: (taskId: string, entryId: string) => void;
}

export const GoalDetail: React.FC<GoalDetailProps> = ({
  project,
  goal,
  onBack,
  onTaskMove,
  onTaskCreate,
  onAddTimeEntry,
  onDeleteTimeEntry,
}) => {
  const [timeSheetTask, setTimeSheetTask] = useState<Task | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    assignee: '',
  });

  const completedTasks = goal.tasks.filter(t => t.status === 'completed').length;
  const totalTasks = goal.tasks.length;
  const progressPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const totalTimeLogged = goal.tasks.reduce((sum, t) => sum + totalTime(t.timeEntries ?? []), 0);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const status = result.destination.droppableId as Task['status'];
    onTaskMove(result.draggableId, goal.id, status, result.destination.index);
  };

  const handleTaskSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onTaskCreate(
      goal.id,
      {
        ...taskForm,
        goalId: goal.id,
        startDate: new Date(taskForm.startDate),
        endDate: new Date(taskForm.endDate),
        status: 'pending',
        order: goal.tasks.length,
      },
      project.id,
    );
    setShowTaskModal(false);
    setTaskForm({ title: '', description: '', startDate: '', endDate: '', assignee: '' });
  };

  return (
    <div className="goal-detail">
      {/* Header */}
      <div className="goal-detail-header">
        <Button variant="outline-secondary" size="sm" onClick={onBack} className="me-3">
          ← Back to Projects
        </Button>
        <div className="goal-detail-breadcrumb">
          <span className="text-muted">{project.title}</span>
          <span className="text-muted mx-2">/</span>
          <span className="fw-semibold">{goal.title}</span>
        </div>
      </div>

      {/* Goal info card */}
      <Card className="goal-detail-card mb-4">
        <Card.Body>
          <Row>
            <Col md={8}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <h4 className="mb-0">{goal.title}</h4>
                <Badge
                  bg="none"
                  style={{ backgroundColor: STATUS_COLORS[goal.status] || '#6c757d' }}
                >
                  {STATUS_LABELS[goal.status as Task['status']] ?? goal.status}
                </Badge>
              </div>
              {goal.description && (
                <p className="text-muted mb-2">{goal.description}</p>
              )}
              <div className="goal-detail-dates text-muted small">
                {formatDate(goal.startDate)} — {formatDate(goal.endDate)}
              </div>
            </Col>
            <Col md={4}>
              <div className="goal-detail-stats">
                <div className="goal-stat">
                  <div className="goal-stat-value">{totalTasks}</div>
                  <div className="goal-stat-label">Tasks</div>
                </div>
                <div className="goal-stat">
                  <div className="goal-stat-value">{completedTasks}</div>
                  <div className="goal-stat-label">Done</div>
                </div>
                <div className="goal-stat">
                  <div className="goal-stat-value">{totalTimeLogged > 0 ? formatDuration(totalTimeLogged) : '—'}</div>
                  <div className="goal-stat-label">Logged</div>
                </div>
              </div>
              <div className="goal-progress mt-2">
                <div className="d-flex justify-content-between mb-1">
                  <small className="text-muted">Progress</small>
                  <small className="fw-semibold">{progressPercent}%</small>
                </div>
                <div className="progress" style={{ height: 8 }}>
                  <div
                    className="progress-bar bg-success"
                    role="progressbar"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Task actions */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Tasks</h5>
        <Button size="sm" variant="primary" onClick={() => setShowTaskModal(true)}>
          + Add Task
        </Button>
      </div>

      {/* Kanban board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="kanban-board">
          {STATUSES.map(status => {
            const columnTasks = goal.tasks.filter(t => t.status === status);
            return (
              <div key={status} className="kanban-column">
                <div className={`kanban-column-header kanban-header-${status}`}>
                  <span>{STATUS_LABELS[status]}</span>
                  <span className="kanban-count">{columnTasks.length}</span>
                </div>
                <Droppable droppableId={status}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`kanban-column-body ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                    >
                      {columnTasks.map((task, index) => {
                        const logged = totalTime(task.timeEntries ?? []);
                        return (
                          <Draggable key={task.id} draggableId={task.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`kanban-card ${snapshot.isDragging ? 'dragging' : ''}`}
                              >
                                <div className="kanban-card-title">{task.title}</div>
                                {task.description && (
                                  <div className="kanban-card-desc">{task.description}</div>
                                )}
                                {task.assignee && (
                                  <div className="kanban-card-assignee">{task.assignee}</div>
                                )}
                                <div className="kanban-card-meta">
                                  <small className="text-muted">
                                    {formatDate(task.startDate)} — {formatDate(task.endDate)}
                                  </small>
                                </div>
                                <div className="kanban-card-footer">
                                  <span className="kanban-card-time">
                                    {logged > 0 ? `⏱ ${formatDuration(logged)}` : '⏱ —'}
                                  </span>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="kanban-card-time-btn"
                                    onClick={e => { e.stopPropagation(); setTimeSheetTask(task); }}
                                  >
                                    Time Sheet
                                  </Button>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>

      {/* Add Task Modal */}
      <Modal show={showTaskModal} onHide={() => setShowTaskModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Task to {goal.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleTaskSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={taskForm.title}
                onChange={e => setTaskForm({ ...taskForm, title: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={taskForm.description}
                onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Assignee</Form.Label>
              <Form.Control
                type="text"
                value={taskForm.assignee}
                onChange={e => setTaskForm({ ...taskForm, assignee: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={taskForm.startDate}
                    onChange={e => setTaskForm({ ...taskForm, startDate: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={taskForm.endDate}
                    onChange={e => setTaskForm({ ...taskForm, endDate: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowTaskModal(false)}>Cancel</Button>
              <Button variant="primary" type="submit">Create Task</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* TimeSheet */}
      {timeSheetTask && (
        <TimeSheet
          task={timeSheetTask}
          show={true}
          onHide={() => setTimeSheetTask(null)}
          onAddEntry={onAddTimeEntry}
          onDeleteEntry={onDeleteTimeEntry}
        />
      )}
    </div>
  );
};
